import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { omit } from 'lodash-es';
import { useState } from 'react';
import { useForm } from 'react-hook-form';

import Label from '~components/FormFields/v2/Label';
import TextInput from '~components/FormFields/v2/TextInput';
import {
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import { setSettlementSchemaDefaultValues } from '~formsShared/components/SettlementFormFields';
import useEffectiveCompanyId from '~hooks/useEffectiveCompanyId/useEffectiveCompanyId';
import { Settlement } from '~hooks/useSettlements/models';
import useSettlements from '~hooks/useSettlements/useSettlements';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';

import { EM_DASH, SETTLEMENT_NOTES_MAX_LENGTH } from './constants';

interface SettlementNotesProps {
  onSettlementChange: (settlement: Settlement) => void;
  settlement: Settlement;
}

const SettlementNotes = ({ onSettlementChange, settlement }: SettlementNotesProps) => {
  const { updateSettlement } = useSettlements();
  const { toasterStore } = useStores();
  const companyId = useEffectiveCompanyId();
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const { control, handleSubmit, reset } = useForm<{ notes: string | null }>({
    defaultValues: { notes: settlement.notes },
  });

  const startEditing = () => {
    reset({ notes: settlement.notes });
    setIsEditing(true);
  };

  const onSave = handleSubmit(async (values) => {
    if (!settlement.id) {
      return;
    }

    // The settlement can be approved by someone else while this editor is open, and the
    // API drops notes on a locked settlement without failing — so the save would report
    // success having changed nothing.
    if (!settlement.editable) {
      toasterStore.push(
        alert(t('error_messages.settlements.failed_to_update_locked'), AlertTypes.error),
      );

      return;
    }

    setIsSaving(true);
    try {
      const updatedSettlement = await updateSettlement(companyId, settlement.id, {
        ...omit(setSettlementSchemaDefaultValues(undefined, settlement), 'dueDate'),
        notes: values.notes?.trim() ? values.notes.trim() : null,
      });
      onSettlementChange(updatedSettlement);
      setIsEditing(false);
    } catch {
      // Error already handled in the hook.
    } finally {
      setIsSaving(false);
    }
  });

  return (
    <Box
      sx={{
        border: (theme) => `1px solid ${theme.semantic.border}`,
        borderRadius: '6px',
        backgroundColor: (theme) => theme.semantic.surfaceRaised,
        p: 2,
        minWidth: '300px',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 1,
        }}
      >
        <Label>{t('form_fields.notes')}</Label>

        {settlement.editable && !isEditing && (
          <IconButton aria-label={t('actions.edit')} size="small" onClick={startEditing}>
            <EditOutlinedIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      {isEditing ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          <TextInput
            control={control}
            name="notes"
            rows={3}
            rules={{
              maxLength: {
                value: SETTLEMENT_NOTES_MAX_LENGTH,
                message: `${t('form_validation_errors.max_length', { field: t('form_fields.notes'), max: SETTLEMENT_NOTES_MAX_LENGTH })}`,
              },
            }}
          />
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 1,
            }}
          >
            <SmallButton
              color="secondary"
              variant="outlined"
              startIcon={<CloseIcon />}
              disabled={isSaving}
              onClick={() => setIsEditing(false)}
            >
              {t('actions.cancel')}
            </SmallButton>
            <SmallLoadingButton
              color="brandV2Yellow"
              startIcon={<CheckIcon />}
              loading={isSaving}
              onClick={onSave}
            >
              {t('actions.save')}
            </SmallLoadingButton>
          </Box>
        </Box>
      ) : (
        <Typography variant="body2">{settlement.notes || EM_DASH}</Typography>
      )}
    </Box>
  );
};

export default SettlementNotes;
